import React, { useState, useEffect } from 'react';
import { 
  MdAdd, 
  MdEdit, 
  MdDelete, 
  MdCalendarToday,
  MdEvent,
  MdSave,
  MdCancel,
  MdWarning,
  MdClose
} from 'react-icons/md';
import api from '../../config/axios';
import { useLanguage } from '../../context/LanguageContext';

const HolidaySettings = () => {
  const { t } = useLanguage();
  // State tanımlamaları
  const [years, setYears] = useState([]); // Yıl tanımları listesi
  const [holidays, setHolidays] = useState([]); // Tatil günleri listesi
  const [selectedYear, setSelectedYear] = useState(null); // Seçili yıl
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Yıl ekleme/düzenleme modal state'leri
  const [showYearModal, setShowYearModal] = useState(false);
  const [editingYear, setEditingYear] = useState(null);
  const [yearForm, setYearForm] = useState({ year: '' });
  
  // Tatil ekleme/düzenleme modal state'leri
  const [showHolidayModal, setShowHolidayModal] = useState(false);
  const [editingHoliday, setEditingHoliday] = useState(null);
  const [holidayForm, setHolidayForm] = useState({
    holiday_start_date: '',
    holiday_end_date: '',
    holiday_description: ''
  });

  // Form validasyon hataları
  const [formErrors, setFormErrors] = useState({});

  // Popup state'i
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm', // 'confirm', 'success', 'error'
    onConfirm: null,
    showCancel: true
  });

  // Component yüklendiğinde verileri getir
  useEffect(() => {
    fetchYears();
  }, []);

  // Seçili yıl değiştiğinde o yıla ait tatilleri getir
  useEffect(() => {
    if (selectedYear) {
      fetchHolidays(selectedYear.id);
    } else {
      setHolidays([]);
    }
  }, [selectedYear]);

  // Yıl tanımlarını getir
  const fetchYears = async () => {
    try {
      setIsLoading(true);
      const token = localStorage.getItem('token');
      const response = await api.get('/year-definitions', {
        headers: { Authorization: `Bearer ${token}` }
      });
      setYears(response.data);
      
      // Eğer seçili yıl yoksa ve yıl listesi varsa, ilk yılı seç
      if (response.data.length > 0 && !selectedYear) {
        setSelectedYear(response.data[0]);
      }
      // Eğer seçili yıl varsa ama artık listede yoksa, seçili yılı temizle
      else if (selectedYear && !response.data.find(year => year.id === selectedYear.id)) {
        setSelectedYear(null);
        setHolidays([]);
      }
    } catch (error) {
      console.error('Yıl tanımları getirilemedi:', error);
      setError(t('holidaySettings.errorLoadingYears'));
    } finally {
      setIsLoading(false);
    }
  };

  // Tatil günlerini getir
  const fetchHolidays = async (yearId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await api.get(`/holiday-definitions/year/${yearId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setHolidays(response.data);
    } catch (error) {
      console.error('Tatil günleri getirilemedi:', error);
      setError(t('holidaySettings.errorLoadingHolidays'));
    }
  };

  // Yıl ekleme/düzenleme işlemleri
  const handleYearSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      if (editingYear) {
        // Yıl düzenleme
        await api.put(`/year-definitions/${editingYear.id}`, yearForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        fetchYears();
      } else {
        // Yeni yıl ekleme
        const response = await api.post('/year-definitions', yearForm, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Yeni eklenen yılı otomatik olarak seç
        if (response.data && response.data.id) {
          setSelectedYear(response.data);
        }
        
        fetchYears();
      }
      closeYearModal();
    } catch (error) {
      console.error('Yıl kaydedilemedi:', error);
      setError(t('holidaySettings.errorSavingYear'));
    }
  };

  // Popup fonksiyonları
  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || t('holidaySettings.confirm'),
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Yıl silme işlemi
  const handleYearDelete = async (yearId, yearName) => {
    showPopup({
      title: t('holidaySettings.confirmYearDeletion'),
      message: t('holidaySettings.confirmYearDeletionMessage', { yearName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await api.delete(`/year-definitions/${yearId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          
          // Eğer silinen yıl seçili yıl ise, seçili yılı temizle
          if (selectedYear && selectedYear.id === yearId) {
            setSelectedYear(null);
            setHolidays([]);
          }
          
          fetchYears();
          closePopup();
          showPopup({
            title: t('holidaySettings.success'),
            message: t('holidaySettings.yearDeletedSuccess', { yearName }),
            type: 'success',
            showCancel: false,
            onConfirm: closePopup
          });
        } catch (error) {
          console.error('Yıl silinemedi:', error);
          closePopup();
          showPopup({
            title: t('holidaySettings.error'),
            message: error.response?.data?.message || t('holidaySettings.errorDeletingYear'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  // Tatil ekleme/düzenleme işlemleri
  const handleHolidaySubmit = async (e) => {
    e.preventDefault();
    if (!selectedYear) {
      setError(t('holidaySettings.pleaseSelectYearFirst'));
      return;
    }

    // Form validasyonu
    const errors = {};
    
    // Başlangıç tarihi kontrolü
    if (!holidayForm.holiday_start_date) {
      errors.holiday_start_date = t('holidaySettings.startDateRequired');
    }
    
    // Bitiş tarihi kontrolü
    if (!holidayForm.holiday_end_date) {
      errors.holiday_end_date = t('holidaySettings.endDateRequired');
    }
    
    // Tarih karşılaştırma kontrolü kaldırıldı - başlangıç ve bitiş tarihi aynı olabilir
    
    // Yıl kontrolü - tarihlerin seçili yıla ait olması gerekiyor
    if (holidayForm.holiday_start_date && selectedYear) {
      const startYear = new Date(holidayForm.holiday_start_date).getFullYear();
      if (startYear !== selectedYear.year) {
        errors.holiday_start_date = t('holidaySettings.startDateMustBelongToYear', { year: selectedYear.year });
      }
    }
    
    if (holidayForm.holiday_end_date && selectedYear) {
      const endYear = new Date(holidayForm.holiday_end_date).getFullYear();
      if (endYear !== selectedYear.year) {
        errors.holiday_end_date = t('holidaySettings.endDateMustBelongToYear', { year: selectedYear.year });
      }
    }
    
    // Açıklama kontrolü
    if (!holidayForm.holiday_description.trim()) {
      errors.holiday_description = t('holidaySettings.holidayDescriptionRequired');
    }
    
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Tarihleri ayarla (varsayılan saat: başlangıç 00:00, bitiş 23:59)
      const startDateTime = `${holidayForm.holiday_start_date}T00:00:00`;
      const endDateTime = `${holidayForm.holiday_end_date}T23:59:59`;
      
      const holidayData = {
        holiday_start_date: startDateTime,
        holiday_end_date: endDateTime,
        holiday_description: holidayForm.holiday_description.trim(),
        year_definition_id: selectedYear.id
      };

      if (editingHoliday) {
        // Tatil düzenleme
        await api.put(`/holiday-definitions/${editingHoliday.id}`, holidayData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      } else {
        // Yeni tatil ekleme
        await api.post('/holiday-definitions', holidayData, {
          headers: { Authorization: `Bearer ${token}` }
        });
      }
      
      setFormErrors({});
      fetchHolidays(selectedYear.id);
      closeHolidayModal();
      
      showPopup({
        title: t('holidaySettings.success'),
        message: editingHoliday ? t('holidaySettings.holidayUpdatedSuccess') : t('holidaySettings.holidayAddedSuccess'),
        type: 'success',
        showCancel: false,
        onConfirm: closePopup
      });
         } catch (error) {
       console.error('Tatil kaydedilemedi:', error);
       setError(t('holidaySettings.errorSavingHoliday'));
     }
  };

  // Tatil silme işlemi
  const handleHolidayDelete = async (holidayId, holidayName) => {
    showPopup({
      title: t('holidaySettings.confirmHolidayDeletion'),
      message: t('holidaySettings.confirmHolidayDeletionMessage', { holidayName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const token = localStorage.getItem('token');
          await api.delete(`/holiday-definitions/${holidayId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          fetchHolidays(selectedYear.id);
          closePopup();
          showPopup({
            title: t('holidaySettings.success'),
            message: t('holidaySettings.holidayDeletedSuccess', { holidayName }),
            type: 'success',
            showCancel: false,
            onConfirm: closePopup
          });
        } catch (error) {
          console.error('Tatil silinemedi:', error);
          closePopup();
          showPopup({
            title: t('holidaySettings.error'),
            message: error.response?.data?.message || t('holidaySettings.errorDeletingHoliday'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  // Modal açma/kapama fonksiyonları
  const openYearModal = (year = null) => {
    setEditingYear(year);
    if (year) {
      setYearForm({ year: year.year.toString() });
    } else {
      setYearForm({ year: '' });
    }
    setShowYearModal(true);
  };

  const closeYearModal = () => {
    setShowYearModal(false);
    setEditingYear(null);
    setYearForm({ year: '' });
  };

  const openHolidayModal = (holiday = null) => {
    setEditingHoliday(holiday);
    setFormErrors({});
    
    if (holiday) {
      // Mevcut tatili düzenleme - sadece tarihi al
      const startDate = new Date(holiday.holiday_start_date);
      const endDate = new Date(holiday.holiday_end_date);
      
      const formData = {
        holiday_start_date: startDate.toISOString().split('T')[0],
        holiday_end_date: endDate.toISOString().split('T')[0],
        holiday_description: holiday.holiday_description
      };
      
      setHolidayForm(formData);
    } else {
      // Yeni tatil ekleme
      const formData = {
        holiday_start_date: '',
        holiday_end_date: '',
        holiday_description: ''
      };
      
      setHolidayForm(formData);
    }
    setShowHolidayModal(true);
  };

  const closeHolidayModal = () => {
    setShowHolidayModal(false);
    setEditingHoliday(null);
    setFormErrors({});
    setHolidayForm({
      holiday_start_date: '',
      holiday_end_date: '',
      holiday_description: ''
    });
  };

  // Tarih formatını düzenle
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('tr-TR');
  };

  // Loading durumu
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Başlık */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            {t('holidaySettings.title')}
          </h1>
      
        </div>
      </div>

      {/* Hata mesajı */}
      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="flex items-center">
            <MdWarning className="text-red-400 mr-2" size={20} />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 ">
                 {/* Yıl Tanımları Bölümü */}
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 ">
           <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-6 border-b border-gray-200 dark:border-gray-600 rounded-tl-lg rounded-tr-lg">
             <div className="flex items-center justify-between">
               <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center ">
                 <MdCalendarToday className="mr-2" size={16} />
                 {t('holidaySettings.yearDefinitions')}
               </h2>
                               <button
                  onClick={() => openYearModal()}
                  className="bg-[#272c39] hover:bg-[#1e2230] text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center text-xs sm:text-sm font-medium transition-colors"
                >
                  <MdAdd className="mr-1" size={14} />
                  <span className="hidden sm:inline">{t('holidaySettings.addYear')}</span>
                  <span className="sm:hidden">Add</span>
                </button>
             </div>
           </div>
          
          <div className="p-3 sm:p-6">
            {years.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400 text-sm">
                {t('holidaySettings.noYearDefinitionsFound')}
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {years.map((year) => (
                  <div
                    key={year.id}
                    className={`p-3 sm:p-4 rounded-lg border cursor-pointer transition-colors ${
                      selectedYear?.id === year.id
                        ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                        : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                    }`}
                    onClick={() => setSelectedYear(year)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <MdCalendarToday className="text-gray-400 mr-2 sm:mr-3" size={16} />
                        <span className="font-medium text-gray-900 dark:text-white text-sm sm:text-base">
                          {year.year}
                        </span>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openYearModal(year);
                          }}
                          className="text-gray-400 hover:text-blue-600 p-1"
                        >
                          <MdEdit size={14} />
                        </button>
                                                 <button
                           onClick={(e) => {
                             e.stopPropagation();
                             handleYearDelete(year.id, year.year);
                           }}
                           className="text-gray-400 hover:text-red-600 p-1"
                         >
                           <MdDelete size={14} />
                         </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

                 {/* Tatil Günleri Bölümü */}
         <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700">
           <div className="bg-gray-50 dark:bg-gray-700 p-3 sm:p-6 border-b border-gray-200 dark:border-gray-600 rounded-tl-lg rounded-tr-lg">
             <div className="flex items-center justify-between">
               <h2 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-white flex items-center">
                 <MdEvent className="mr-2" size={16} />
                 {t('holidaySettings.holidays')}
                 {selectedYear && (
                   <span className="ml-2 text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                     ({selectedYear.year})
                   </span>
                 )}
               </h2>
               {selectedYear && (
                                   <button
                    onClick={() => openHolidayModal()}
                    className="bg-[#272c39] hover:bg-[#1e2230] text-white px-2 sm:px-4 py-1.5 sm:py-2 rounded-lg flex items-center text-xs sm:text-sm font-medium transition-colors"
                  >
                    <MdAdd className="mr-1" size={14} />
                    <span className="hidden sm:inline">{t('holidaySettings.addHoliday')}</span>
                    <span className="sm:hidden">Add</span>
                  </button>
               )}
             </div>
           </div>
          
          <div className="p-3 sm:p-6">
            {!selectedYear ? (
              <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400 text-sm">
                {t('holidaySettings.pleaseSelectYearFirst')}
              </div>
            ) : holidays.length === 0 ? (
              <div className="text-center py-6 sm:py-8 text-gray-500 dark:text-gray-400 text-sm">
                {t('holidaySettings.noHolidaysDefinedForThisYear')}
              </div>
            ) : (
              <div className="space-y-2 sm:space-y-3">
                {holidays.map((holiday) => (
                  <div
                    key={holiday.id}
                    className="p-3 sm:p-4 rounded-lg border border-gray-200 dark:border-gray-700"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 dark:text-white mb-1 text-sm sm:text-base truncate">
                          {holiday.holiday_description}
                        </h3>
                        <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400">
                          {formatDate(holiday.holiday_start_date)} - {formatDate(holiday.holiday_end_date)}
                        </p>
                      </div>
                      <div className="flex items-center space-x-1 sm:space-x-2 ml-2">
                        <button
                          onClick={() => openHolidayModal(holiday)}
                          className="text-gray-400 hover:text-blue-600 p-1"
                        >
                          <MdEdit size={14} />
                        </button>
                                                 <button
                           onClick={() => handleHolidayDelete(holiday.id, holiday.holiday_description)}
                           className="text-gray-400 hover:text-red-600 p-1"
                         >
                           <MdDelete size={14} />
                         </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Yıl Ekleme/Düzenleme Modal */}
      {showYearModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{editingYear ? t('holidaySettings.editYear') : t('holidaySettings.addNewYear')}</h3>
              <button
                onClick={closeYearModal}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleYearSubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('holidaySettings.year')}
                  </label>
                  <input
                    type="number"
                    value={yearForm.year}
                    onChange={(e) => setYearForm({ year: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder="2024"
                    required
                    min="1900"
                    max="2100"
                  />
                </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeYearModal}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('holidaySettings.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {editingYear ? t('holidaySettings.update') : t('holidaySettings.addYear')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Tatil Ekleme/Düzenleme Modal */}
      {showHolidayModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{editingHoliday ? t('holidaySettings.editHoliday') : t('holidaySettings.addNewHoliday')}</h3>
              <button
                onClick={closeHolidayModal}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={24} />
              </button>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleHolidaySubmit} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    {t('holidaySettings.holidayDescription')}
                  </label>
                  <input
                    type="text"
                    value={holidayForm.holiday_description}
                    onChange={(e) => setHolidayForm({ ...holidayForm, holiday_description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                    placeholder={t('holidaySettings.newYearHoliday')}
                    required
                  />
                  {formErrors.holiday_description && (
                    <p className="text-red-500 text-xs mt-1">{formErrors.holiday_description}</p>
                  )}
                </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t('holidaySettings.startDate')}
                   </label>
                   <input
                     type="date"
                     value={holidayForm.holiday_start_date}
                     onChange={(e) => setHolidayForm({ ...holidayForm, holiday_start_date: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                   {formErrors.holiday_start_date && (
                     <p className="text-red-500 text-xs mt-1">{formErrors.holiday_start_date}</p>
                   )}
                 </div>
                                 <div>
                   <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                     {t('holidaySettings.endDate')}
                   </label>
                   <input
                     type="date"
                     value={holidayForm.holiday_end_date}
                     onChange={(e) => setHolidayForm({ ...holidayForm, holiday_end_date: e.target.value })}
                     className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                     required
                   />
                   {formErrors.holiday_end_date && (
                     <p className="text-red-500 text-xs mt-1">{formErrors.holiday_end_date}</p>
                   )}
                 </div>

                <div className="flex justify-end gap-3">
                  <button
                    type="button"
                    onClick={closeHolidayModal}
                    className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                  >
                    {t('holidaySettings.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230]"
                  >
                    {editingHoliday ? t('holidaySettings.update') : t('holidaySettings.addHoliday')}
                  </button>
                </div>
              </form>
            </div>
                     </div>
         </div>
       )}

       {/* Genel Popup */}
       {popup.show && (
         <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
           <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
             {/* Header */}
             <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
               <h3 className="text-lg font-medium">{popup.title}</h3>
             </div>
             
             <div className="p-8 bg-white dark:bg-gray-800">
               <div className="text-center">
                 <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                   {popup.message}
                 </p>
                 <div className="flex justify-center gap-4">
                   {popup.showCancel && (
                     <button
                       onClick={closePopup}
                       className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                     >
                       {t('holidaySettings.cancel')}
                     </button>
                   )}
                   <button
                     onClick={() => {
                       if (popup.onConfirm) {
                         popup.onConfirm();
                       } else {
                         closePopup();
                       }
                     }}
                     className={`px-4 py-2 text-white rounded-md ${
                       popup.type === 'error' 
                         ? 'bg-red-600 hover:bg-red-700'
                         : popup.type === 'success'
                         ? 'bg-green-600 hover:bg-green-700'
                         : 'bg-[#272c39] hover:bg-[#1e2230]'
                     }`}
                   >
                     {popup.type === 'confirm' ? t('holidaySettings.confirm') : t('holidaySettings.ok')}
                   </button>
                 </div>
               </div>
             </div>
           </div>
         </div>
       )}
     </div>
   );
 };

export default HolidaySettings;
